<template>
  <Transition name="alert">
    <div v-if="show" :class="alertClasses" role="alert">
      <!-- Icon -->
      <div v-if="showIcon" class="flex-shrink-0">
        <slot name="icon">
          <i v-if="variant === 'success'" class="fas fa-check-circle text-lg"></i>
          <i v-else-if="variant === 'danger'" class="fas fa-times-circle text-lg"></i>
          <i v-else-if="variant === 'warning'" class="fas fa-exclamation-triangle text-lg"></i>
          <i v-else class="fas fa-info-circle text-lg"></i>
        </slot>
      </div>

      <!-- Content -->
      <div class="flex-1 ml-3">
        <h4 v-if="title" :class="titleClasses">{{ title }}</h4>
        <p :class="messageClasses">
          <slot>{{ message }}</slot>
        </p>
        <!-- Actions -->
        <div v-if="$slots.actions" class="mt-2">
          <slot name="actions"></slot>
        </div>
      </div>

      <!-- Close button -->
      <button
        v-if="closable"
        type="button"
        class="flex-shrink-0 ml-auto -mr-1 -mt-1 p-1 rounded-lg focus:outline-none transition-colors"
        :class="closeButtonClasses"
        @click="handleClose"
      >
        <i class="fas fa-times text-sm"></i>
      </button>
    </div>
  </Transition>
</template>

<script>
export default {
  name: 'BaseAlert',
  props: {
    show: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'info',
      validator: (value) => ['info', 'success', 'warning', 'danger'].includes(value)
    },
    title: {
      type: String,
      default: null
    },
    message: {
      type: String,
      default: null
    },
    closable: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    rounded: {
      type: String,
      default: 'lg',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    outlined: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  computed: {
    alertClasses() {
      return [
        'flex items-start transition-all duration-200',
        this.sizeClasses,
        this.variantClasses,
        this.roundedClasses
      ]
    },
    sizeClasses() {
      const sizes = {
        sm: 'p-3 text-sm',
        md: 'p-4 text-sm',
        lg: 'p-5 text-base'
      }
      return sizes[this.size]
    },
    variantClasses() {
      if (this.outlined) {
        const outlinedVariants = {
          info: 'bg-white border border-blue-200 text-blue-700',
          success: 'bg-white border border-green-200 text-green-700',
          warning: 'bg-white border border-yellow-200 text-yellow-700',
          danger: 'bg-white border border-red-200 text-red-700'
        }
        return outlinedVariants[this.variant]
      }

      const variants = {
        info: 'bg-blue-50 border border-blue-100 text-blue-700',
        success: 'bg-green-50 border border-green-100 text-green-700',
        warning: 'bg-yellow-50 border border-yellow-100 text-yellow-700',
        danger: 'bg-red-50 border border-red-100 text-red-700'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      const rounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-lg',
        lg: 'rounded-xl',
        xl: 'rounded-2xl'
      }
      return rounded[this.rounded]
    },
    titleClasses() {
      const classes = {
        info: 'font-semibold text-blue-800 mb-1',
        success: 'font-semibold text-green-800 mb-1',
        warning: 'font-semibold text-yellow-800 mb-1',
        danger: 'font-semibold text-red-800 mb-1'
      }
      return classes[this.variant]
    },
    messageClasses() {
      return 'm-0'
    },
    closeButtonClasses() {
      const classes = {
        info: 'text-blue-400 hover:bg-blue-100',
        success: 'text-green-400 hover:bg-green-100',
        warning: 'text-yellow-400 hover:bg-yellow-100',
        danger: 'text-red-400 hover:bg-red-100'
      }
      return classes[this.variant]
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.alert-enter-active,
.alert-leave-active {
  transition: all 0.2s ease;
}

.alert-enter-from,
.alert-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
